import { projects } from '@/data/projects';

/**
 * Journey scroll lengths, derived from `projects.length` so the per-snap
 * distance stays constant when projects are added or removed.
 *
 * Background: the hijack travels station-to-station at a fixed pace
 * (`HIJACK.journey.travelSeconds`). If the corridor scroll length is
 * fixed but the station count changes, each snap covers a different
 * amount of scroll → the visual pace doesn't match the timing budget
 * any more (symptom: with 8 projects the journey feels rushed compared
 * to 7). Anchoring on `VH_PER_STATION` instead of total length keeps
 * the cadence consistent.
 *
 * Station count is `intro + N projects + end portal = N + 2`, so the
 * corridor has `N + 1` SEGMENTS between consecutive stations — that's
 * the count we multiply by. `HANDOFF_LENGTH_VH` is appended after the
 * corridor for the EndPortal gallery rotation; it's a fixed window
 * regardless of N.
 *
 * Single source of truth — both CameraRig (3D scrub + pin length) and
 * DesktopJourneyScaffold (the SSR min-height that reserves the layout
 * space before tier resolves) import from here. The previous setup
 * duplicated `1400` and `1600` across the JS pin config and a CSS rule,
 * which silently went out of sync the moment a project was added.
 */

// Tuning constants — desktop feels comfortable at ~175vh per snap with
// the current HIJACK travel duration (1.0s). Mobile is paced shorter so
// fast-swipe gestures don't overshoot.
const VH_PER_STATION_DESKTOP = 175;
const VH_PER_STATION_MOBILE = 125;

// Trailing scroll window reserved for the EndPortal gallery rotation —
// camera is parked at the portal during this slice. Fixed regardless of
// project count.
export const HANDOFF_LENGTH_VH = 100;

// Station count drives every derived length.
const SEGMENT_COUNT = projects.length + 1; // intro→p0, p0→p1, …, pN-1→portal

export const CORRIDOR_LENGTH_VH_DESKTOP =
  VH_PER_STATION_DESKTOP * SEGMENT_COUNT;
export const CORRIDOR_LENGTH_VH_MOBILE =
  VH_PER_STATION_MOBILE * SEGMENT_COUNT;

export const SCROLL_LENGTH_VH_DESKTOP =
  CORRIDOR_LENGTH_VH_DESKTOP + HANDOFF_LENGTH_VH;
export const SCROLL_LENGTH_VH_MOBILE =
  CORRIDOR_LENGTH_VH_MOBILE + HANDOFF_LENGTH_VH;

// `min-height` of `.journey-page` — must reserve layout for the
// journey-container (100vh) + corridor + handoff so SSR doesn't shove
// the InfoSection below into the wrong place before ScrollTrigger
// mounts. See the DesktopJourneyScaffold pending-tier branch.
export const JOURNEY_PAGE_MIN_VH = 100 + SCROLL_LENGTH_VH_DESKTOP;
